import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { Flex, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { openUrl } from '@/utils/external-resources'
import { StActivityCardContainer, StActivityCardContent, StDescription } from '../ActivityCard.styled'
import { RemecareWarningModal } from './RemecareWarningModal'
import { useRemecareWarning } from './useRemecareWarning'

import type { TimelineActivity } from '@/modules/timeline/components/timeline/Timeline'

export const RemecareCard = ({
  id,
  type,
  scheduledTime,
  chip,
  completed,
  description,
  isActive = true,
  hasPassed,
  hasExpired,
  showColorBorder = true,
  monotone = false,
  meta,
}: TimelineActivity) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const itemIsSelected = location.pathname.includes(id.toString())

  const { remecareWarningState, setRemecareWarningState } = useRemecareWarning()
  const [showModal, setShowModal] = useState(false)
  const { showErrorToast } = useToast()

  const onClick = () => {
    if (isActive) {
      if (Capacitor.isNativePlatform()) {
        if (remecareWarningState) {
          onRemecareOpen(true)
        } else {
          mixpanel.track(`${location.pathname}_remecare_modal_open`)
          setShowModal(true)
        }
      } else {
        mixpanel.track(`${location.pathname}_remecare_detail_open`)
        navigate(`/timeline/activity/${id}`)
      }
    }
  }

  const onRemecareOpen = useCallback(
    (neverShow: boolean) => {
      setRemecareWarningState(neverShow)
      mixpanel.track(`${location}_remecare_open`, {
        neverShow,
        url: meta?.remecareUrl,
      })
      if (meta?.remecareUrl) {
        openUrl(meta?.remecareUrl, {
          windowName: '_blank',
          forceDeeplinks: true,
        })
      } else {
        showErrorToast({
          message: t({
            id: 'timeline.remecare-warning-modal.error',
            message: 'Geen geldige Remecare URL gevonden. Probeer het later opnieuw.',
          }),
        })
      }
    },
    [location, meta?.remecareUrl, setRemecareWarningState, showErrorToast],
  )

  return (
    <>
      <StActivityCardContainer
        type={type}
        hasPassed={hasPassed}
        monotone={monotone}
        showColorBorder={showColorBorder}
        selected={itemIsSelected}
        completed={!!completed}
        onClick={onClick}
      >
        <div id={`card_${id}`} style={{ position: 'absolute', top: '-56px' }} />
        <FlexRow alignItems='center'>
          <FontAwesomeIcon icon={['fal', 'clipboard-check']} />
          <Margin ml={theme.UI.Spacing.Space2} />

          <FlexRow flexGrow={1} alignItems='center' justifyContent='space-between'>
            <H4 color={isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText} noMargin>
              {i18n._(activityTypeTranslations.Task as string)}
            </H4>
            {!completed && scheduledTime ? (
              <Span type='BodyLargeBold'>{formatLocale(scheduledTime, 'HH:mm')}</Span>
            ) : null}

            {chip ? (
              <Chip type={chip.type}>
                {chip.icon ? (
                  <FontAwesomeIcon icon={chip.icon} style={{ paddingRight: theme.UI.Spacing.Space2 }} />
                ) : null}
                <Span type='BodySmallRegular'>{chip.message}</Span>
              </Chip>
            ) : null}
          </FlexRow>
        </FlexRow>

        {description ? (
          <>
            <StActivityCardContent>
              {description.map((value, index) => {
                return (
                  <Flex justifyContent='space-between' alignItems='center' key={`${value}_${index}`}>
                    <StDescription
                      type={value.type}
                      color={
                        value.color ??
                        (isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText)
                      }
                    >
                      {value.text}
                    </StDescription>

                    {index === description.length - 1 && !completed && !hasExpired && isActive ? (
                      <FontAwesomeIcon icon={['far', 'chevron-right']} fontSize={16} />
                    ) : null}
                  </Flex>
                )
              })}
            </StActivityCardContent>
          </>
        ) : null}
      </StActivityCardContainer>

      <RemecareWarningModal
        isVisible={showModal}
        onClose={() => setShowModal(false)}
        onAction={(neverShow: boolean) => {
          setShowModal(false)
          onRemecareOpen(neverShow)

          if (neverShow) {
            mixpanel.track(`${location.pathname}_remecare_never_show`)
          }
        }}
      />
    </>
  )
}
